import { WhiteBox, FoodsIcon } from "@foodpilot/foods";
import { Stack, Box, Typography, Button, CircularProgress, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { usePropertyContext } from "../context/usePropertyContext";
import { MAX_FILE_SIZE_MB } from "../context/hooks/attachmentLimits";

export const DragAndDrop = () => {
    const theme = useTheme();
    const { t } = useTranslation();

    const { attachmentState, post } = usePropertyContext();
    const canAnswerSsq = post.canAnswerSsq === true;
    const { fileInputRef, triggerFileInput } = attachmentState;

    const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
        if (!canAnswerSsq) {
            event.target.value = "";
            return;
        }

        const files = event.target.files;
        if (files !== null && files.length > 0) {
            attachmentState.addTemporaryFiles(files);
        }

        event.target.value = "";
    };

    const handleDrop = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
        if (!canAnswerSsq) {
            return;
        }

        const files = event.dataTransfer?.files;
        if (files !== null && files.length > 0) {
            attachmentState.addTemporaryFiles(files);
        }
    };

    const handleDragOver = (event: React.DragEvent<HTMLDivElement>) => {
        event.preventDefault();
    };

    return (
        <Stack width={"100%"} display={{ xs: "none", md: "block" }}>
            <WhiteBox
                sx={{
                    width: "100%",
                    border: `dashed ${theme.palette.grey[500]}`,
                }}
                borderRadius={theme.spacing(1)}
                onDragOver={canAnswerSsq ? handleDragOver : undefined}
                onDrop={canAnswerSsq ? handleDrop : undefined}
            >
                {!attachmentState.isUploading && (
                    <Stack alignItems="center" padding={theme.spacing(4, 4)} gap={theme.spacing(1)}>
                        <Box display="flex" alignItems="center" sx={{ color: theme.palette.grey[900] }}>
                            <FoodsIcon icon="uploadFile" size={8} />
                        </Box>
                        {canAnswerSsq && (
                            <Box display="flex" alignItems="center">
                                <Typography variant="body-regular">
                                    {t("property.drop_files_from_computer", "Drop your files here or")}
                                </Typography>
                                <Button
                                    variant="text"
                                    sx={{
                                        cursor: "pointer",
                                        marginLeft: theme.spacing(0.5),
                                        lineHeight: "normal",
                                    }}
                                    onClick={triggerFileInput}
                                >
                                    {t("property.upload_files_from_computer", "Upload files here")}
                                </Button>
                            </Box>
                        )}
                        <Typography variant="body2" color="textSecondary">
                            {t("property.file_upload_info", "Supported formats : PDF, docx, pptx, xlsx, PNG, JPEG")}
                        </Typography>
                        <Typography variant="body2" color="textSecondary">
                            {t("property.file_upload_size_info", "Maximum size : {{maxSize}} MB", {
                                maxSize: MAX_FILE_SIZE_MB,
                            })}
                        </Typography>
                    </Stack>
                )}
                {attachmentState.isUploading && (
                    <Stack alignItems="center" padding={theme.spacing(6)}>
                        <CircularProgress size={50} color="primary" />
                        <Typography variant="h4">
                            {t("property.attachment.uploading", "Uploading your file…")}
                        </Typography>
                    </Stack>
                )}
            </WhiteBox>

            {canAnswerSsq && (
                <input
                    type="file"
                    multiple
                    ref={fileInputRef}
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                />
            )}
        </Stack>
    );
};
